import { Close } from "@faire/slate-icons/Close";
import { Message } from "@faire/slate/foundation/color";
import { IAction, renderTextAction } from "./buttonUtils";
import { Color } from "@faire/web/slate/Color";
import { Row } from "@faire/web/slate/Layout";
import { Spacer, getSpacing } from "@faire/web/slate/spacing";
import { Typography } from "@faire/web/slate/Typography";
import * as React from "react";
import styled, { DefaultTheme, useTheme } from "styled-components";

const Wrapper = styled(Row).attrs({ align: "center", justify: "center" })<{
  $color?: Color;
}>`
  background: ${(props) =>
    props.$color ??
    props.theme.banners?.alert?.color ??
    Message.surface.warning};
  padding: ${getSpacing(12, "4x")};
  width: 100%;
  position: relative;
`;

const CloseButton = styled.button`
  position: absolute;
  right: 16px;
  display: flex;
  font-size: 20px;
`;

export interface AlertBannerProps {
  color?: Color;
  message: string | React.ReactNode;
  textColor?: Color;
  alertAction?: IAction;
  onClose?: () => void;
  className?: string;
  centerAlignIcon?: boolean;
}

export const AlertBanner: React.FC<AlertBannerProps> = ({
  color,
  onClose,
  textColor,
  message,
  alertAction,
  className,
  centerAlignIcon,
}) => {
  const theme = useTheme();
  const alertActionComponent = renderTextAction(alertAction, {
    textColor: textColor ?? theme?.banners?.alert?.textColor,
  });
  return (
    <Wrapper $color={color} className={className}>
      {typeof message === "string" ? (
        <Row align={centerAlignIcon ? "center" : undefined}>
          {renderEventsIcon(theme)}
          <Typography
            color={textColor ?? theme?.banners?.alert?.textColor}
            align="center"
          >
            {message}
            {alertActionComponent ? <> {alertActionComponent}</> : null}
          </Typography>
        </Row>
      ) : (
        <Row align={centerAlignIcon ? "center" : undefined}>
          {renderEventsIcon(theme)}
          {message}
          {alertActionComponent ? (
            <>
              <Spacer size={4} />
              {alertActionComponent}
            </>
          ) : null}
        </Row>
      )}
      {onClose ? (
        <CloseButton type="button" onClick={onClose}>
          <Close fontSize="16px" />
        </CloseButton>
      ) : null}
    </Wrapper>
  );
};

const renderEventsIcon = (theme: DefaultTheme) => {
  if (!theme?.eventIcon) {
    return null;
  }

  const Component = theme.eventIcon.Component;

  return (
    <Component
      data-test-id="alert-banner-event-icon"
      width="20px"
      height="20px"
      margin="0 8px 0 0"
    />
  );
};
